/**
 * Types d'étapes dans le processus de création d'un bon
 */
export enum DecisionVoucherV2Step {
  QUALIFICATION = "QUALIFICATION",  // Étape 1: Qualification du dossier
  CONFIGURATION = "CONFIGURATION",   // Étape 2: Configuration (accord ou refus)
  VALIDATION = "VALIDATION",         // Étape 3: Validation et envoi
}

/**
 * Types de décisions possibles
 */
export enum DecisionVoucherV2Type {
  PENDING = "PENDING",                     // En attente de décision
  PARTIAL_OR_TOTAL_AGREEMENT = "PARTIAL_OR_TOTAL_AGREEMENT",  // Accord partiel ou total
  REFUSAL = "REFUSAL",                     // Refus
  COMMERCIAL_GESTURE = "COMMERCIAL_GESTURE", // Geste commercial
}

/**
 * État de sauvegarde du bon
 */
export enum DecisionVoucherV2Status {
  DRAFT = "DRAFT",       // Brouillon
  FINAL = "FINAL",       // Version finale
  SENT = "SENT",         // Envoyé
  SIGNED = "SIGNED",     // Signé
}

/**
 * Interface pour les critères d'évaluation d'éligibilité
 */
export interface IQualificationCriteria {
  isContractValid: boolean | null;  // Le contrat est-il valide (date non expirée)
  isKilometrageLimitRespected: boolean | null;  // La limite kilométrique est-elle respectée
  isDeclarationTimeRespected: boolean | null;  // La déclaration a-t-elle été faite dans les délais
  arePartsEligible: boolean | null;  // Les pièces sont-elles couvertes par la garantie

  // Informations additionnelles
  contractEndDate?: string;         // Date de fin du contrat
  currentKilometrage?: number;      // Kilométrage actuel
  kilometrageLimit?: number;        // Limite kilométrique du contrat
  declarationDate?: string;         // Date de la déclaration
  failureDate?: string;             // Date de la panne
  declarationDelay?: number;        // Délai de déclaration (en jours)

  // Explications/justifications fournies par l'utilisateur
  contractValidityJustification?: string;
  kilometrageJustification?: string;
  declarationTimeJustification?: string;
  partsEligibilityJustification?: string;

  // Recommandation automatique générée
  recommendedDecision?: DecisionVoucherV2Type;

  // Décision finale de l'utilisateur
  finalDecision?: DecisionVoucherV2Type;
  finalDecisionJustification?: string;
}

/**
 * Interface pour les détails des montants
 */
export interface IAmountDetailV2 {
  id: string;  // ID unique (généré côté client)
  ref: string;  // Référence
  label: string;  // Libellé
  quantity: number;  // Quantité
  unitPrice: number;  // Prix unitaire
  amountTotalHT: number;  // Montant total HT
  amountTotalTTC: number;  // Montant total TTC
  amountSeller: number;  // Montant pris en charge par le vendeur (garantie)
  amountSellerCommercial: number;  // Montant pris en charge par le vendeur (commercial)
  comment?: string;  // Commentaire sur la ligne
  // Pourcentage de prise en charge (calculé automatiquement ou défini manuellement)
  coveragePercentage?: number;
  isSelected: boolean;  // Si la ligne est sélectionnée pour traitement
}

/**
 * Interface pour la configuration de l'accord
 */
export interface IAgreementConfigurationV2 {
  amountDetails: IAmountDetailV2[];  // Détails des montants
  totalAmountTTC: number;  // Montant total TTC
  totalAmountHT: number;  // Montant total HT
  totalAmountSeller: number;  // Montant total pris en charge par le vendeur (garantie)
  totalAmountSellerCommercial: number;  // Montant total pris en charge par le vendeur (commercial)
  totalAmountCustomer: number;  // Montant restant à la charge du client
  comment: string;  // Commentaire général sur l'accord
  partsIds?: number[];  // IDs des pièces associées
}

/**
 * Types prédéfinis de motifs de refus
 */
export enum RefusalReasonType {
  CONTRACT_EXPIRED = "CONTRACT_EXPIRED",  // Contrat expiré
  KILOMETRAGE_EXCEEDED = "KILOMETRAGE_EXCEEDED",  // Kilométrage dépassé
  PARTS_NOT_COVERED = "PARTS_NOT_COVERED",  // Pièces non couvertes
  MAINTENANCE_ISSUE = "MAINTENANCE_ISSUE",  // Défaut d'entretien
  IMPROPER_USE = "IMPROPER_USE",  // Utilisation non conforme
  LATE_DECLARATION = "LATE_DECLARATION",  // Déclaration tardive
  LATE_QUOTE = "LATE_QUOTE",  // Devis tardif
  NO_PRIOR_APPROVAL = "NO_PRIOR_APPROVAL",  // Absence d'accord préalable
  OTHER = "OTHER",  // Autre
}

/**
 * Interface pour la configuration du refus
 */
export interface IRefusalConfigurationV2 {
  reasonType: RefusalReasonType | null;  // Type de motif de refus
  reasonDetails: string;  // Détails du motif
  templateId?: string;  // ID du template d'email utilisé
  emailSubject?: string;  // Sujet de l'email
  emailContent?: string;  // Contenu de l'email
  templateVariables?: Record<string, any>;  // Variables du template
  attachments?: number[];  // Pièces jointes (IDs de documents)
}

/**
 * Types de méthodes de signature
 */
export enum SignatureMethodV2 {
  ELECTRONIC = "ELECTRONIC",  // Signature électronique
  MANUAL = "MANUAL",  // Signature manuelle
}

/**
 * Types de destinataires
 */
export enum RecipientTypeV2 {
  SELLER = "SELLER",  // Vendeur
  CUSTOMER = "CUSTOMER",  // Client
  REPAIRER = "REPAIRER",  // Réparateur
}

/**
 * Interface pour un destinataire
 */
export interface IRecipientV2 {
  type: RecipientTypeV2;  // Type de destinataire
  email?: string;  // Email (optionnel, peut être récupéré automatiquement)
  name?: string;  // Nom (optionnel, peut être récupéré automatiquement)
  message?: string;  // Message personnalisé pour ce destinataire
}

/**
 * Interface pour la configuration d'envoi et signature
 */
export interface ISendingConfigurationV2 {
  signatureMethod: SignatureMethodV2 | null;  // Méthode de signature
  recipients: IRecipientV2[];  // Destinataires
  scheduledDate?: string;  // Date programmée d'envoi
  paymentMethod?: string;  // Méthode de paiement (pour la signature électronique)
  signatureLinks?: Record<RecipientTypeV2, string>;  // Liens de signature par type de destinataire
  sentDates?: Record<RecipientTypeV2, string>;  // Dates d'envoi par type de destinataire
  signedDates?: Record<RecipientTypeV2, string>;  // Dates de signature par type de destinataire
}

/**
 * Interface complète pour le DecisionVoucher V2
 */
export interface IDecisionVoucherV2 {
  // Méta-données
  id?: string;  // ID unique (généré côté client)
  savId: number;  // ID du SAV
  createdAt: string;  // Date de création
  updatedAt: string;  // Date de dernière modification

  // État général
  step: DecisionVoucherV2Step;  // Étape actuelle du processus
  type: DecisionVoucherV2Type;  // Type de décision
  status: DecisionVoucherV2Status;  // État de sauvegarde

  // Contenu par étape
  qualification: IQualificationCriteria;  // Étape 1: Qualification
  agreementConfiguration?: IAgreementConfigurationV2;  // Étape 2a: Configuration de l'accord
  refusalConfiguration?: IRefusalConfigurationV2;  // Étape 2b: Configuration du refus
  sendingConfiguration?: ISendingConfigurationV2;  // Étape 3: Configuration d'envoi

  // Références aux documents
  decisionVoucherDocId?: number | null;  // ID du document généré (bon d'accord/refus)

  // Historique et versions
  version: number;  // Numéro de version
  parentId?: string;  // ID du parent (pour les versions)
}

/**
 * Fonction pour créer un objet decisionVoucherV2 vide initialisé
 */
export function createEmptyDecisionVoucherV2(savId: number): IDecisionVoucherV2 {
  const now = new Date().toISOString();

  return {
    savId,
    createdAt: now,
    updatedAt: now,
    step: DecisionVoucherV2Step.QUALIFICATION,
    type: DecisionVoucherV2Type.PENDING,
    status: DecisionVoucherV2Status.DRAFT,
    qualification: {
      isContractValid: null,
      isKilometrageLimitRespected: null,
      isDeclarationTimeRespected: null,
      arePartsEligible: null,
    },
    version: 1,
  };
}

/**
 * Fonction pour calculer les totaux à partir des détails des montants
 */
export function calculateTotals(amountDetails: IAmountDetailV2[]): {
  totalAmountTTC: number;
  totalAmountHT: number;
  totalAmountSeller: number;
  totalAmountSellerCommercial: number;
  totalAmountCustomer: number;
} {
  const selectedDetails = amountDetails.filter(detail => detail.isSelected);

  const totalAmountTTC = selectedDetails.reduce((sum, detail) => sum + detail.amountTotalTTC, 0);
  const totalAmountHT = selectedDetails.reduce((sum, detail) => sum + detail.amountTotalHT, 0);
  const totalAmountSeller = selectedDetails.reduce((sum, detail) => sum + detail.amountSeller, 0);
  const totalAmountSellerCommercial = selectedDetails.reduce((sum, detail) => sum + detail.amountSellerCommercial, 0);
  const totalAmountCustomer = totalAmountTTC - totalAmountSeller - totalAmountSellerCommercial;

  return {
    totalAmountTTC,
    totalAmountHT,
    totalAmountSeller,
    totalAmountSellerCommercial,
    totalAmountCustomer,
  };
}